import { Controller } from "@hotwired/stimulus";

// Offres promotionnelles du « Détail de l'acquisition » (issue #699).
//
// La remise appartient au couple (utilisateur, fiche bien) et non au profil
// client : elle ne passe donc pas par `api-sync`, qui n'achemine que des
// critères. Le champ est piloté dans les deux sens ici :
//  - à chaque réponse du pipeline `listing_details`, il reprend le montant
//    renvoyé par le backend (qui le relit en base) et se masque tant qu'aucune
//    fiche persistée n'est chargée ;
//  - à la saisie, il persiste la valeur sur une route SAAS dédiée puis force un
//    recalcul du seul scope `listing_details`.
//
// Le contrôleur est posé sur `.simulator-panel`, comme `favorites` : les
// évènements `remote:update:success` sont émis sur les colonnes (« event
// sinks ») et remontent jusqu'à cet ancêtre commun.
export default class extends Controller {
  static targets = ["container", "input"];
  static values = {
    saveUrl: String,
    debounce: { type: Number, default: 400 },
  };

  connect() {
    this.onRemoteSuccess = (e) => this.handleRemoteSuccess(e);
    this.element.addEventListener("remote:update:success", this.onRemoteSuccess);
  }

  disconnect() {
    try { this.element.removeEventListener("remote:update:success", this.onRemoteSuccess); } catch (_) {}
    if (this._saveTimer) clearTimeout(this._saveTimer);
  }

  // ---------- Saisie ----------
  // Action du champ (`input` et `change`) : debounce puis persistance.
  save() {
    if (this._saveTimer) clearTimeout(this._saveTimer);
    this._saveTimer = setTimeout(() => this.persistAndRefresh(), this.debounceValue);
  }

  persistAndRefresh() {
    this._saveTimer = null;
    if (!this.hasInputTarget || !this.saveUrlValue) return;
    const listingId = this.activeListingId();
    // Pas de fiche persistée : la remise n'a rien à quoi se rattacher.
    if (!listingId) return;

    const amount = this.currentAmount();
    // Évite qu'une réponse `listing_details` en vol ne réécrive le champ avec
    // l'ancien montant pendant que la saisie est persistée.
    this._pendingAmount = amount;

    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    const csrf = this.csrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;

    fetch(this.saveUrlValue, {
      method: "POST",
      headers,
      credentials: "same-origin",
      body: JSON.stringify({ listing_id: listingId, amount }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        this.requestRecalculation();
      })
      .catch(() => {})
      .finally(() => {
        this._pendingAmount = null;
      });
  }

  // Le payload envoyé à `get_details` ne change pas (la remise est relue en
  // base), d'où le `force` qui court-circuite la déduplication d'`api-sync`.
  requestRecalculation() {
    try {
      this.element.dispatchEvent(new CustomEvent("api-sync:refresh", {
        bubbles: true,
        detail: { scope: "listing_details", force: true },
      }));
    } catch (_) {}
  }

  // ---------- Réponses du backend ----------
  handleRemoteSuccess(e) {
    if (e?.detail?.channel !== "listing_details") return;
    const buyingDetails = e?.detail?.json?.buying_details;
    if (!buyingDetails) return;

    this.toggleVisibility(Boolean(buyingDetails.promotional_offer_visible));
    this.applyAmount(buyingDetails.promotional_offer);
  }

  toggleVisibility(visible) {
    if (!this.hasContainerTarget) return;
    this.containerTarget.classList.toggle("d-none", !visible);
  }

  applyAmount(amount) {
    if (!this.hasInputTarget) return;
    // Saisie en cours (debounce ou requête en vol) : la valeur du champ fait foi.
    if (this._saveTimer || this._pendingAmount != null) return;
    if (document.activeElement === this.inputTarget) return;

    const value = Number(amount || 0);
    if (value === this.currentAmount()) return;

    this.inputTarget.value = String(Math.round(value));
    // `number-format` possède l'affichage du champ (séparateurs, unité « € »)
    // et son `data-raw` : on lui délègue la normalisation.
    try {
      this.inputTarget.dispatchEvent(new CustomEvent("number-format:normalize"));
    } catch (_) {}
  }

  // ---------- Helpers ----------
  currentAmount() {
    if (!this.hasInputTarget) return 0;
    const raw = this.inputTarget.dataset.raw;
    const parsed = Number(raw === undefined || raw === "" ? 0 : raw);
    return Number.isNaN(parsed) ? 0 : Math.max(Math.round(parsed), 0);
  }

  activeListingId() {
    const field = this.element.querySelector("[data-listing-results-target='listingId']");
    const value = field && field.value;
    return value ? String(value) : "";
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : null;
  }
}
